import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useReviews } from "../hooks/useSchools";
import { useReviews as useReviewsHook } from "../hooks/useReviews";
import { useAuthStore } from "../store/authStore";
import CommentsList from "../features/comments/CommentsList";
import RatingStars from "../components/RatingStars";
import Avatar from "../components/Avatar";

export default function ReviewDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getReview } = useReviews();
  const { deleteReview } = useReviewsHook();
  const { user } = useAuthStore();

  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (id) {
      loadReviewData();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadReviewData = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await getReview(id);

      if (result && result.success !== false) {
        // Handle different response structures from backend
        const reviewData =
          result.review || result.data?.review || result.data || result;
        setReview(reviewData);
      } else {
        const errorMsg = result?.error || "Không tìm thấy đánh giá";
        console.error("Review fetch failed:", errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error("Exception when loading review:", err);
      setError("Đã xảy ra lỗi khi tải đánh giá");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    setDeleting(true);
    try {
      const result = await deleteReview(id);
      if (result.success) {
        // Redirect to school detail page after successful deletion
        navigate(`/schools/${getSchoolId()}`, {
          replace: true,
          state: { message: "Đánh giá đã được xóa thành công" },
        });
      } else {
        setError(result.error || "Không thể xóa đánh giá");
      }
    } catch (err) {
      console.error("Error deleting review:", err);
      setError("Đã xảy ra lỗi khi xóa đánh giá");
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSchoolId = () => {
    return review?.school?._id || review?.school?.id;
  };

  const getReviewId = () => {
    return review?._id || review?.id;
  };

  const isReviewOwner = () => {
    return (
      user &&
      review &&
      (user._id === review.user?._id ||
        user.id === review.user?.id ||
        user._id === review.user ||
        user.id === review.user)
    );
  };

  if (loading) {
    return (
      <div className="review-detail">
        <div className="review-detail__container">
          <div className="review-detail__loading">
            <div className="spinner"></div>
            <p>Đang tải...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-detail">
        <div className="review-detail__container">
          <div className="review-detail__error">
            <h2>Lỗi</h2>
            <p>{error}</p>
            <button onClick={() => navigate(-1)} className="btn btn--secondary">
              Quay lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="review-detail">
        <div className="review-detail__container">
          <div className="review-detail__not-found">
            <h2>Không tìm thấy đánh giá</h2>
            <button onClick={() => navigate(-1)} className="btn btn--secondary">
              Quay lại
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-detail">
      <div className="review-detail__container">
        {/* Breadcrumb */}
        <div className="review-detail__breadcrumb">
          <Link to="/" className="breadcrumb-link">
            Trang chủ
          </Link>
          <span className="breadcrumb-separator">›</span>
          <Link to="/reviews" className="breadcrumb-link">
            Đánh giá
          </Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">Chi tiết đánh giá</span>
        </div>

        {/* Main Review Content */}
        <div className="review-detail__content">
          {/* Header */}
          <div className="review-detail__header">
            <div className="review-detail__school-info">
              <Link
                to={`/schools/${getSchoolId()}`}
                className="review-detail__school-name"
              >
                {review.school?.name}
              </Link>
              <span className="review-detail__school-level">
                {review.school?.level}
              </span>
            </div>

            <div className="review-detail__rating">
              <RatingStars rating={review.overallRating} />
              <span className="review-detail__rating-text">
                {review.overallRating}/5
              </span>
            </div>
          </div>

          {/* Title */}
          <h1 className="review-detail__title">{review.title}</h1>

          {/* Author and Date */}
          <div className="review-detail__meta">
            <div className="review-detail__author">
              <Avatar
                user={review.user}
                size="md"
                className="review-detail__avatar"
              />
              <div className="review-detail__author-info">
                <span className="review-detail__author-name">
                  {review.user?.name || "Người dùng"}
                </span>
                <span className="review-detail__date">
                  {formatDate(review.createdAt)}
                </span>
              </div>
            </div>

            {/* Owner Actions */}
            {isReviewOwner() && (
              <div className="review-detail__owner-actions">
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn btn--danger btn--small"
                  disabled={deleting}
                >
                  <span className="btn__icon">🗑️</span>
                  Xóa đánh giá
                </button>
              </div>
            )}
          </div>

          {/* Detailed Ratings */}
          <div className="review-detail__ratings">
            <h3 className="review-detail__ratings-title">Đánh giá chi tiết</h3>
            <div className="review-detail__ratings-grid">
              <div className="rating-item">
                <span className="rating-item__label">
                  Chất lượng giảng dạy:
                </span>
                <div className="rating-item__value">
                  <RatingStars rating={review.ratings?.teaching || 0} />
                  <span>{review.ratings?.teaching || 0}/5</span>
                </div>
              </div>
              <div className="rating-item">
                <span className="rating-item__label">Cơ sở vật chất:</span>
                <div className="rating-item__value">
                  <RatingStars rating={review.ratings?.facilities || 0} />
                  <span>{review.ratings?.facilities || 0}/5</span>
                </div>
              </div>
              <div className="rating-item">
                <span className="rating-item__label">
                  Hoạt động ngoại khóa:
                </span>
                <div className="rating-item__value">
                  <RatingStars rating={review.ratings?.activities || 0} />
                  <span>{review.ratings?.activities || 0}/5</span>
                </div>
              </div>
              <div className="rating-item">
                <span className="rating-item__label">Môi trường học tập:</span>
                <div className="rating-item__value">
                  <RatingStars rating={review.ratings?.environment || 0} />
                  <span>{review.ratings?.environment || 0}/5</span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="review-detail__text">
            <h3 className="review-detail__content-title">Nội dung đánh giá</h3>
            <p className="review-detail__content-text">{review.content}</p>
          </div>

          {/* Actions */}
          <div className="review-detail__actions">
            <div className="review-detail__helpful">
              <button className="btn btn--ghost">
                👍 Hữu ích ({review.helpful?.length || 0})
              </button>
              <button className="btn btn--ghost">
                👎 Không hữu ích ({review.notHelpful?.length || 0})
              </button>
            </div>

            <Link to={`/schools/${getSchoolId()}`} className="btn btn--primary">
              Xem thông tin trường
            </Link>
          </div>
        </div>

        {/* Comments Section */}
        <div className="review-detail__comments">
          <CommentsList reviewId={getReviewId()} />
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal__header">
                <h3>Xác nhận xóa đánh giá</h3>
              </div>
              <div className="modal__content">
                <p>Bạn có chắc chắn muốn xóa đánh giá này không?</p>
                <p className="modal__warning">
                  ⚠️ Hành động này không thể hoàn tác!
                </p>
              </div>
              <div className="modal__actions">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn btn--secondary"
                  disabled={deleting}
                >
                  Hủy
                </button>
                <button
                  onClick={handleDeleteReview}
                  className="btn btn--danger"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span className="btn__spinner"></span>
                      Đang xóa...
                    </>
                  ) : (
                    <>
                      <span className="btn__icon">🗑️</span>
                      Xóa đánh giá
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
